import React from 'react';
import { CellContext } from '@tanstack/react-table';
import cn from 'classnames';

import { AdaptiveTableCell, GroupSelectItemInterface, GroupSelectOptionsInterface, Icons } from '@components/common';
import Actions from '@components/common/Actions';

import { IKpiAlertSeverityOutDTO } from '@interfaces/kpi/dto/kpiAlertsDTO';
import { IGroupSelectCategoryGroup, IGroupSelectOptionsKpi } from '@components/common/GroupSelectKpi/GroupSelect.types';

import {
	EKpiNotificationsStatusLabels,
	ESeverity,
} from '@enums/kpi-common';

import { columnSizes } from '@constants/table';

import styles from './KpiAlertRulesTable/KpiAlertRulesTable.module.scss';

interface IGetColumnsProps {
	openNotesModal: (value: string, id: string) => void;
	openEditForm: (id: string) => void;
	onDelete: (ids: string[]) => void;
	notesId?: string | null;
}

export function getColumns({
	openNotesModal,
	openEditForm,
	onDelete,
	notesId,
}: IGetColumnsProps) {
	return [
		{
			header: 'Rule name',
			accessorKey: 'name',
			cell: ({ getValue }: CellContext<any, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'Severity',
			accessorKey: 'severity',
			cell: ({ getValue }: CellContext<IKpiAlertSeverityOutDTO, any>) => {
				const value = getValue();

				return (
					<div className={styles.severityCell}>
						<div
							className={cn(styles.colorLabel, {
								[styles.warning]: value.name === ESeverity.warning,
								[styles.error]: value.name === ESeverity.error,
								[styles.critical]: value.name === ESeverity.critical,
								[styles.informational]: value.name === ESeverity.informational,
							})}
						/>
						<AdaptiveTableCell text={value.title} />
					</div>
				);
			},
		},
		{
			header: 'Status',
			accessorKey: 'isActive',
			cell: ({ getValue }: CellContext<any, any>) => {
				const value = getValue();

				return (
					<AdaptiveTableCell text={
						value === true
							? EKpiNotificationsStatusLabels.active
							: EKpiNotificationsStatusLabels.inactive
					}
					/>
				);
			},
		},
		{
			header: 'Description',
			accessorKey: 'description',
			enableSorting: false,
			cell: ({ getValue, row }: CellContext<any, any>) => {
				const value = getValue();
				const onNotesClick = () => openNotesModal(value, row.original.id);

				return (
					<div className={styles.notesColumn}>
						{value && <div
							className={styles.notesIcon}
							onClick={onNotesClick}
						>
							{Icons.summaryIcon({ isActive: notesId === row.original.id })}
						</div>
						}
						<div className={styles.notes}>
							{value}
						</div>
					</div>
				);
			},
		},
		{
			header: 'Actions',
			accessorKey: 'actions',
			enableSorting: false,
			size: columnSizes.size35,
			cell: ({ row }: CellContext<any, any>) => {
				const handleEdit = () => {
					openEditForm(row.original.id);
				};

				const toggleDeleteConfirm = () => {
					onDelete([row.original.id]);
				};

				return (
					<div className={styles.action}>
						<Actions
							deleteTooltip='Delete rule'
							editHandler={handleEdit}
							editTooltip='Edit this rule'
							handleDelete={toggleDeleteConfirm}
							isDelete
							isEdit
						/>
					</div>
				);
			},
		},
	];
}

export const mergeOptionsWithChosenValues = (
	options: GroupSelectOptionsInterface[] = [],
	optionsValues: GroupSelectOptionsInterface[] = [],
) => {
	return options.map((option: GroupSelectOptionsInterface) => {
		const hasCheckedItem = optionsValues?.find((item: GroupSelectOptionsInterface): boolean =>
			item.category.name === option.category.name);

		if (hasCheckedItem) {
			return {
				...option,
				items: option.items.map(item => {
					const isChecked: boolean | undefined = optionsValues
						?.some((site: GroupSelectOptionsInterface) => site.items
							.find((siteItem: GroupSelectItemInterface) => siteItem.id === item.id));

					return {
						...item,
						checked: isChecked,
					};
				}),
			};
		}

		return option;
	});
};

export const mergeKpiOptionsWithChosenValues = (
	options: IGroupSelectOptionsKpi[] = [],
	optionsValues: IGroupSelectOptionsKpi[] = [],
): IGroupSelectOptionsKpi[] => {
	return options.map((option: IGroupSelectOptionsKpi) => {
		const hasCheckedItem = option.categoryGroups.some((categoryGroup: IGroupSelectCategoryGroup) => {
			return optionsValues?.some((item: any) => item.categoryGroup === categoryGroup);
		});

		if (hasCheckedItem) {
			return {
				...option,
				categoryGroups: option.categoryGroups.map((categoryGroup: any) => {
					const isChecked: boolean | undefined = optionsValues
						?.some((site: any) => site.categoryGroup === categoryGroup);

					return {
						...categoryGroup,
						checked: isChecked,
					};
				}),
			};
		}

		return option;
	});
};

export const addCheckedProperty = (data: GroupSelectOptionsInterface[]): GroupSelectOptionsInterface[] => {
	return data.map(topLevel => ({
		...topLevel,
		checked: true,
		items: topLevel.items.map(subCategory => ({
			...subCategory,
			checked: true,
			items: subCategory?.items?.map(item => ({
				...item,
				checked: true,
			})),
		})),
	}));
};
